import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import type { GenerationStatsTally, GenerationStatsUnits } from '@mpr/shared';

import { api } from '../lib/api.js';
import {
  buildBars,
  costDisplay,
  daysInRange,
  formatUsd,
  groupFor,
  RANGE_PRESETS,
  rangeForDays,
  unitParts,
  type CostDisplay,
} from '../lib/usageStats.js';
import { useFormatters, useI18n } from '../i18n/LanguageProvider.js';
import type { TranslationKey } from '../i18n/translations.js';
import { ErrorNotice } from './ErrorNotice.js';
import { Icon } from './Icons.js';

/**
 * What the studio produced and what it cost, for a period.
 *
 * Every figure is an estimate (the prices set on each connector times what
 * each job consumed), and the screen says so. A result whose connector has no
 * price is shown as "not priced", never as zero: zero would read as good news
 * the numbers cannot back.
 */

type Preset = (typeof RANGE_PRESETS)[number] | 'custom';

/** A cost as the reader should see it: a number, "not priced", or both when some jobs were left out. */
export function CostText({ display }: { display: CostDisplay }) {
  const { t } = useI18n();
  if (display.kind === 'none') return <>-</>;
  if (display.kind === 'unpriced') return <span className="badge warn">{t('usage.notPriced')}</span>;
  return (
    <>
      <span className="usage-money" dir="ltr">
        {formatUsd(display.costUsd)}
      </span>
      {display.kind === 'partial' && (
        <span className="badge warn usage-partial">{t('usage.partial', { count: display.unknown })}</span>
      )}
    </>
  );
}

function UnitsText({ units }: { units: GenerationStatsUnits }) {
  const { t } = useI18n();
  const fmt = useFormatters();
  const parts = unitParts(units);
  if (parts.length === 0) return <>-</>;
  return (
    <>
      {parts.map((part) => (
        <div key={part.kind} className="tiny">
          {part.kind === 'tokens'
            ? t('usage.unit.tokens', { input: fmt.number(part.input), output: fmt.number(part.output) })
            : t(`usage.unit.${part.kind}` as TranslationKey, { value: fmt.number(part.value) })}
        </div>
      ))}
    </>
  );
}

export function UsagePanel() {
  const { t } = useI18n();
  const fmt = useFormatters();
  const [preset, setPreset] = useState<Preset>(30);
  const initial = rangeForDays(30);
  const [customFrom, setCustomFrom] = useState(initial.from);
  const [customTo, setCustomTo] = useState(initial.to);

  const range = preset === 'custom' ? { from: customFrom, to: customTo } : rangeForDays(preset);
  const valid = Boolean(range.from) && Boolean(range.to) && range.from <= range.to;
  const groupBy = groupFor(valid ? daysInRange(range.from, range.to) : 30);

  const query = useQuery({
    queryKey: ['generation-stats', range.from, range.to, groupBy],
    queryFn: () => api.generationStats({ from: range.from, to: range.to, groupBy }),
    enabled: valid,
  });
  const stats = query.data;

  const tallyRow = (row: GenerationStatsTally) => (
    <>
      <td data-label={t('usage.col.jobs')}>{fmt.number(row.done)}</td>
      <td data-label={t('usage.col.failed')}>{row.failed > 0 ? fmt.number(row.failed) : '-'}</td>
      <td data-label={t('usage.col.cost')}>
        <CostText display={costDisplay(row)} />
      </td>
    </>
  );

  return (
    <div className="usage-panel">
      <p className="muted small">{t('usage.estimateNote')}</p>

      <div className="filters">
        <div className="preset-row">
          {RANGE_PRESETS.map((days) => (
            <button
              key={days}
              type="button"
              className={preset === days ? 'subtab active' : 'subtab'}
              onClick={() => setPreset(days)}
            >
              {t('insights.lastDays', { count: days })}
            </button>
          ))}
          <button
            type="button"
            className={preset === 'custom' ? 'subtab active' : 'subtab'}
            onClick={() => setPreset('custom')}
          >
            {t('insights.custom')}
          </button>
        </div>
        {preset === 'custom' && (
          <div className="date-row">
            <label htmlFor="usage-from">{t('insights.from')}</label>
            <input
              id="usage-from"
              type="date"
              value={customFrom}
              max={customTo}
              onChange={(event) => setCustomFrom(event.target.value)}
            />
            <label htmlFor="usage-to">{t('insights.to')}</label>
            <input
              id="usage-to"
              type="date"
              value={customTo}
              min={customFrom}
              onChange={(event) => setCustomTo(event.target.value)}
            />
          </div>
        )}
      </div>

      {query.isLoading && <div className="loading">{t('common.loading')}</div>}
      {query.isError && <ErrorNotice error={query.error} />}

      {stats && (
        <>
          <div className="stat-grid">
            <div className="stat-card accent">
              <span className="stat-label">{t('usage.totalCost')}</span>
              <span className="stat-value">
                {stats.totals.done > 0 && stats.totals.unknownPriceJobs >= stats.totals.done
                  ? t('usage.notPriced')
                  : formatUsd(stats.totals.costUsd)}
              </span>
            </div>
            <div className="stat-card">
              <span className="stat-label">{t('usage.jobsDone')}</span>
              <span className="stat-value">{fmt.number(stats.totals.done)}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">{t('usage.jobsFailed')}</span>
              <span className="stat-value">{fmt.number(stats.totals.failed)}</span>
            </div>
            <div className={stats.totals.unknownPriceJobs > 0 ? 'stat-card usage-warn' : 'stat-card'}>
              <span className="stat-label">{t('usage.unpriced')}</span>
              <span className="stat-value">{fmt.number(stats.totals.unknownPriceJobs)}</span>
            </div>
          </div>

          {stats.totals.unknownPriceJobs > 0 ? (
            <p className="usage-note warn small" role="note">
              <span>{t('usage.unpricedHint', { count: stats.totals.unknownPriceJobs })}</span>
            </p>
          ) : (
            stats.totals.done > 0 && <p className="muted small">{t('usage.allPriced')}</p>
          )}
          {stats.totals.testJobs > 0 && (
            <p className="muted small">
              {t('usage.testCost', { cost: formatUsd(stats.totals.testCostUsd), count: stats.totals.testJobs })}
            </p>
          )}

          {stats.totals.jobs === 0 && (
            <div className="empty-state">
              <Icon.sparkle />
              <p>{t('usage.empty')}</p>
            </div>
          )}

          {stats.totals.jobs > 0 && (
            <>
              <CostChart rows={stats.byDay} groupBy={stats.range.groupBy} />

              <h3 className="usage-heading">{t('usage.byConnector')}</h3>
              <div className="grid-wrap">
                <table className="grid usage-grid">
                  <thead>
                    <tr>
                      <th>{t('usage.col.name')}</th>
                      <th>{t('usage.col.provider')}</th>
                      <th>{t('usage.col.jobs')}</th>
                      <th>{t('usage.col.failed')}</th>
                      <th>{t('usage.col.cost')}</th>
                      <th>{t('usage.col.used')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.byConnector.map((row) => (
                      <tr key={row.connectorId ?? 'none'}>
                        <td data-primary dir="auto">
                          <strong>{row.name}</strong>{' '}
                          <span className="badge accent">
                            {t(`studioConnectors.capability.${row.capability}` as TranslationKey)}
                          </span>
                        </td>
                        <td dir="auto" data-label={t('usage.col.provider')}>
                          {row.provider}
                        </td>
                        {tallyRow(row)}
                        <td data-label={t('usage.col.used')}>
                          <UnitsText units={row.units} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <h3 className="usage-heading">{t('usage.byProvider')}</h3>
              <div className="grid-wrap">
                <table className="grid usage-grid">
                  <thead>
                    <tr>
                      <th>{t('usage.col.provider')}</th>
                      <th>{t('usage.col.jobs')}</th>
                      <th>{t('usage.col.failed')}</th>
                      <th>{t('usage.col.cost')}</th>
                      <th>{t('usage.col.used')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.byProvider.map((row) => (
                      <tr key={row.provider}>
                        <td data-primary dir="auto">
                          <strong>{row.provider}</strong>
                        </td>
                        {tallyRow(row)}
                        <td data-label={t('usage.col.used')}>
                          <UnitsText units={row.units} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <h3 className="usage-heading">{t('usage.byDestination')}</h3>
              {stats.byDestination.length === 0 ? (
                <p className="muted small">{t('usage.destinationsEmpty')}</p>
              ) : (
                <div className="grid-wrap">
                  <table className="grid usage-grid">
                    <thead>
                      <tr>
                        <th>{t('usage.col.name')}</th>
                        <th>{t('usage.col.network')}</th>
                        <th>{t('usage.col.projects')}</th>
                        <th>{t('usage.col.jobs')}</th>
                        <th>{t('usage.col.cost')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.byDestination.map((row) => (
                        <tr key={row.destinationId}>
                          <td data-primary dir="auto">
                            <strong>{row.name}</strong>
                          </td>
                          <td data-label={t('usage.col.network')}>{row.networkName}</td>
                          <td data-label={t('usage.col.projects')}>{fmt.number(row.projects)}</td>
                          <td data-label={t('usage.col.jobs')}>{fmt.number(row.jobs)}</td>
                          <td data-label={t('usage.col.cost')}>
                            <CostText
                              display={costDisplay({
                                costUsd: row.costUsd,
                                done: row.jobs,
                                unknownPriceJobs: row.unknownPriceJobs,
                              })}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="muted small">
                {t('usage.attribution', { cost: formatUsd(stats.unpublished.costUsd) })}
              </p>

              <h3 className="usage-heading">{t('usage.byProject')}</h3>
              <div className="grid-wrap">
                <table className="grid usage-grid">
                  <thead>
                    <tr>
                      <th>{t('usage.col.name')}</th>
                      <th>{t('usage.col.jobs')}</th>
                      <th>{t('usage.col.cost')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.byProject.map((row) => (
                      <tr key={row.projectId}>
                        <td data-primary dir="auto">
                          <strong>{row.title}</strong>
                        </td>
                        <td data-label={t('usage.col.jobs')}>{fmt.number(row.jobs)}</td>
                        <td data-label={t('usage.col.cost')}>
                          <CostText
                            display={costDisplay({
                              costUsd: row.costUsd,
                              done: row.jobs,
                              unknownPriceJobs: row.unknownPriceJobs,
                            })}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Cost per bucket as plain CSS bars: heights are fractions of the tallest, a
 * dot marks a bucket with unpriced jobs (its bar is a lower bound), and the
 * time axis stays left to right in Arabic as digits do.
 */
function CostChart({
  rows,
  groupBy,
}: {
  rows: Parameters<typeof buildBars>[0];
  groupBy: 'day' | 'week' | 'month';
}) {
  const { t } = useI18n();
  const { bars, max } = buildBars(rows);

  return (
    <figure className="usage-chart">
      <figcaption className="usage-heading">{t(`usage.chartTitle.${groupBy}` as TranslationKey)}</figcaption>
      {max === 0 ? (
        <p className="muted small">{t('usage.chartEmpty')}</p>
      ) : (
        <>
          <div
            className="usage-bars"
            dir="ltr"
            role="img"
            aria-label={t('usage.chartAria', { max: formatUsd(max) })}
          >
            {bars.map((bar) => (
              <div
                key={bar.day}
                className="usage-bar-col"
                title={t('usage.bar', { day: bar.day, cost: formatUsd(bar.costUsd), count: bar.jobs })}
              >
                <div className="usage-bar" style={{ height: `${Math.round(bar.ratio * 100)}%` }} />
                {bar.unknownPriceJobs > 0 && <span className="usage-bar-dot" />}
              </div>
            ))}
          </div>
          <div className="usage-axis tiny muted" dir="ltr">
            <span>{bars[0]?.day}</span>
            <span>{formatUsd(max)}</span>
            <span>{bars[bars.length - 1]?.day}</span>
          </div>
        </>
      )}
    </figure>
  );
}
